import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BookOpen, CircleUserRound, Info, LogIn, Menu, Newspaper, UserPlus, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Tooltip from "../Tooltip";
import StreakBadge from "../StreakBadge";
import BadgeHistory from "../BadgeHistory";
import ThemeDropdown from "./ThemeDropdown";

const DAILY_GOAL_MINUTES = Object.freeze({
  "30sec": 0.5,
  "2min": 2,
  "5min": 5,
  "10min": 10,
});

function getDailyGoalMinutes(dailyTime) {
  return DAILY_GOAL_MINUTES[dailyTime] ?? DAILY_GOAL_MINUTES["2min"];
}

function formatClock(totalSeconds) {
  const safe = Math.max(0, Math.round(Number(totalSeconds) || 0));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

function formatDailyProgress(progressMinutes, dailyTime) {
  const goalMinutes = getDailyGoalMinutes(dailyTime);
  return `${formatClock((Number(progressMinutes) || 0) * 60)} / ${formatClock(goalMinutes * 60)}`;
}

function formatAccessibleProgress(progressMinutes, dailyTime) {
  const goalMinutes = getDailyGoalMinutes(dailyTime);
  const currentSeconds = Math.max(0, Math.round((Number(progressMinutes) || 0) * 60));
  const goalSeconds = Math.round(goalMinutes * 60);

  const formatSeconds = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes === 0) return `${seconds} seconde${seconds > 1 ? "s" : ""}`;
    if (seconds === 0) return `${minutes} minute${minutes > 1 ? "s" : ""}`;

    return `${minutes} minute${minutes > 1 ? "s" : ""} et ${seconds} seconde${
      seconds > 1 ? "s" : ""
    }`;
  };

  return `${formatSeconds(currentSeconds)} sur ${formatSeconds(goalSeconds)}`;
}

function getReserveLabels(dailyTime) {
  const goalSeconds = getDailyGoalMinutes(dailyTime) * 60;

  return [`${formatClock(goalSeconds)} / ${formatClock(goalSeconds)}`, `${formatClock(Math.max(goalSeconds + 540, 600))} / ${formatClock(goalSeconds)}`];
}

export default function TopbarLeftOnly({
  user,
  currentStreak = 0,
  longestStreak = 0,
  unlockedBadges = [],
  activeDaysCount = 0,
  dailyProgressMin = 0,
  dailyTime = "2min",
  progressPhase = "",
  onOpenProgress,
  isPremium = false,
  suspendTooltips = false,
}) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showBadgeHistory, setShowBadgeHistory] = useState(false);

  const wrapperRef = useRef(null);
  const location = useLocation();
  const isLoggedIn = Boolean(user);
  const homeTooltipsEnabled = location.pathname === "/" && !showBadgeHistory && !suspendTooltips;

  const safeUnlockedBadges = useMemo(
    () => (Array.isArray(unlockedBadges) ? unlockedBadges : []),
    [unlockedBadges],
  );

  const dailyGoalMin = getDailyGoalMinutes(dailyTime);
  const dailyPercent = Math.min(
    100,
    Math.max(0, ((Number(dailyProgressMin) || 0) / dailyGoalMin) * 100),
  );
  const dailyLabel = formatDailyProgress(dailyProgressMin, dailyTime);
  const dailyAriaLabel = formatAccessibleProgress(dailyProgressMin, dailyTime);

    const reserveLabels = useMemo(() => getReserveLabels(dailyTime), [dailyTime]);
  const [displayedLabel, setDisplayedLabel] = useState(dailyLabel);
  const [labelPhase, setLabelPhase] = useState("in");

  useLayoutEffect(() => {
    if (dailyLabel === displayedLabel) return undefined;

    // Recalage technique avant l'animation : aucun fondu, aucun clignotement.
    if (progressPhase && progressPhase !== "fill") {
      setDisplayedLabel(dailyLabel);
      setLabelPhase("in");
      return undefined;
    }

    setLabelPhase("out");

    const id = window.setTimeout(() => {
      setDisplayedLabel(dailyLabel);
      setLabelPhase("in");
    }, 240);

    return () => window.clearTimeout(id);
  }, [dailyLabel, displayedLabel, progressPhase]);


  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);


  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  return (
    <>
      <header className={`topbar topbar--home ${scrolled ? "scrolled" : ""}`}>
        <div className="topbar-left topbar-left--home" ref={wrapperRef}>
          <button
            type="button"
            className="menu-button menu-button--home"
            onClick={() => setOpen((previous) => !previous)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            aria-haspopup="true"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>

          {isLoggedIn ? (
            <div
              className="topbar-progress-badges"
            >
              <div className="topbar-progress-badges__streak">
<Tooltip
  id="streak-badge-help"
  text="Votre série reflète les jours où vous avez pris un moment pour vous. Touchez-la pour retrouver vos badges."
  placement="bottom"
  priority={100}
  showDelay={700}
  enabled={homeTooltipsEnabled}
  sequence="home-guidance"
  order={1}
>
                  <StreakBadge
                    currentStreak={currentStreak}
                    onClick={() => {
                      setOpen(false);
                      setShowBadgeHistory(true);
                    }}
                  />
                </Tooltip>
              </div>
<Tooltip
  id="daily-progress-help"
  text="Ce repère suit le temps consacré à votre objectif du jour. Touchez-le pour voir votre progression."
  placement="bottom"
  priority={90}
  showDelay={700}
  enabled={homeTooltipsEnabled}
  sequence="home-guidance"
  order={2}
>
                <button
                  type="button"
                  className={`topbar-progress-badges__daily ${progressPhase ? "is-revealing" : ""} ${progressPhase && progressPhase !== "fill" ? "is-holding" : ""} ${progressPhase === "fill" && dailyPercent >= 100 ? "is-complete" : ""}`}
                  onClick={() => {
                    setOpen(false);
                    onOpenProgress?.();
                  }}
                  aria-label={`Voir ma progression du jour : ${dailyAriaLabel}`}
                  title="Voir ma progression"
                  data-label={displayedLabel}
                  style={{ "--daily-progress": `${dailyPercent}%` }}
                >
                  <span className="topbar-progress-badges__daily-content">
                    {reserveLabels.map((label) => (<span key={label} className="topbar-progress-badges__daily-reserve" aria-hidden="true">{label}</span>))}
                    <span className={`topbar-progress-badges__daily-label ${labelPhase === "out" ? "is-out" : ""}`}>{displayedLabel}</span>
                  </span>
                </button>
              </Tooltip>
            </div>
          ) : (
            <div className="topbar-auth-actions">
              <Link to="/auth/login" className="topbar-auth-link">
                Se connecter
              </Link>
              <Link to="/auth/signup" className="topbar-auth-signup">
                S&apos;inscrire
              </Link>
            </div>
          )}

          {open && (
            <div className="dropdown-menu glassy">
<Link to="/apropos" className="dropdown-item" onClick={() => setOpen(false)}>
  <Info size={18} />
  <span>À propos</span>
</Link>

              <Link to="/guides" className="dropdown-item" onClick={() => setOpen(false)}>
                <BookOpen size={18} />
                <span>Guides</span>
              </Link>

              {!isLoggedIn && (
                <Link to="/blog" className="dropdown-item" onClick={() => setOpen(false)}>
                  <Newspaper size={18} />
                  <span>Blog</span>
                </Link>
              )}

              {isLoggedIn && (
                <Link
                  to="/profil"
                  className="dropdown-item"
                  onClick={() => setOpen(false)}
                >
                  <CircleUserRound size={18} />
                  <span>Profil</span>
                </Link>
              )}
              
              {!isLoggedIn && (
                <>
                  <Link
                    to="/auth/login"
                    className="dropdown-item"
                    onClick={() => setOpen(false)}
                  >
                    <LogIn size={18} />
                    <span>Se connecter</span>
                  </Link>

                  <Link
                    to="/auth/signup"
                    className="dropdown-item"
                    onClick={() => setOpen(false)}
                  >
                    <UserPlus size={18} />
                    <span>S&apos;inscrire</span>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>

        <div className="topbar-right topbar-right--home">
<Tooltip
  id="theme-toggle"
  text="Adaptez l’apparence de votre refuge: mode clair ou sombre et choix du fond."
  placement="bottom"
  priority={80}
  showDelay={700}
  enabled={homeTooltipsEnabled}
  sequence="home-guidance"
  order={3}
  maxWidth={260}
  viewportMargin={12}
>
            <ThemeDropdown longestStreak={longestStreak} isPremium={isPremium} />
          </Tooltip>
        </div>
      </header>

      {showBadgeHistory && (
        <BadgeHistory
          isOpen={showBadgeHistory}
          onClose={() => setShowBadgeHistory(false)}
          currentStreak={currentStreak}
          longestStreak={longestStreak}
          activeDaysCount={activeDaysCount}
          unlockedBadges={safeUnlockedBadges}
        />
      )}

      <style>{`
        .topbar-left--home {
          gap: 7px;
          min-width: 0;
          height: 36px;
        }

        .menu-button--home {
          width: 36px;
          height: 36px;
          min-width: 36px;
          min-height: 36px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }

        .topbar--home .dropdown-menu {
          top: calc(100% + 8px);
          left: 0;
        }
        /* =========================================================
           BADGES DE PROGRESSION — DEUX CAPSULES INDÉPENDANTES
           ========================================================= */

        .topbar-progress-badges {
          --tb-auria-core: #76a5a7;
          --tb-auria-ring: #4b7cf6;
          --tb-badge-h: 36px;
          --tb-badge-px: 11px;
          --tb-badge-font: 12.5px;
          --tb-badge-gap: 5px;
          --tb-badge-emoji: 15px;
          --tb-badge-text-shift: 1px;
          --tb-badge-emoji-shift: 0px;

          min-width: 0;
          height: 36px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          flex: 0 1 auto;
        }

        /* Le badge de série conserve entièrement le thème du palier
           fourni par StreakBadge : fond, bordure, texte et reflet. */
        .topbar-progress-badges__streak {

          min-width: 0;
          height: 36px;

          display: inline-flex;
          align-items: center;
          flex: 0 0 auto;
        }

        .topbar-progress-badges__streak > * {
          height: 100%;
          display: inline-flex;
          align-items: center;
        }

        .topbar-progress-badges__streak .streak-badge {
          margin: 0 !important;
          vertical-align: middle;
        }

        /* Progression quotidienne : le badge lui-même se remplit. */
        .topbar-progress-badges__daily {
          position: relative;
          isolation: isolate;
          overflow: hidden;

          min-width: 116px;
          height: 36px;
          min-height: 36px;
          padding: 0 13px;

          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;

          border: 1.5px solid rgba(75, 124, 246, 0.16);
          border-radius: 999px;

          background: linear-gradient(180deg, rgba(118, 165, 167, 0.09), rgba(75, 124, 246, 0.06));
          color: #314456;

          box-shadow:
            0 3px 10px rgba(49, 72, 66, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.72),
            inset 0 0 0 1px rgba(255, 255, 255, 0.34);

          cursor: pointer;
          white-space: nowrap;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;

          transition:
            transform 180ms ease,
            border-color 180ms ease,
            box-shadow 180ms ease;
        }

        .topbar-progress-badges__daily::before {
          content: "";
          position: absolute;
          z-index: -1;
          inset: 0 auto 0 0;
          width: var(--daily-progress, 0%);
          max-width: 100%;
          border-radius: inherit;
          background: linear-gradient(90deg, var(--tb-auria-core), var(--tb-auria-ring));
          transition: width 420ms cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }

                .topbar-progress-badges__daily::after {
          content: attr(data-label);
          position: absolute;
          z-index: 2;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-family: var(--font-sans);
          font-size: var(--tb-badge-font, 12.5px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.01em;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          clip-path: inset(0 calc(100% - var(--daily-progress, 0%)) 0 0);
          transform: translateY(var(--tb-badge-text-shift, 1px));
          transition: clip-path 420ms cubic-bezier(0.22, 1, 0.36, 1);
          pointer-events: none;
        }

        .topbar-progress-badges__daily:hover {
          transform: translateY(-1px) scale(1.02);
          border-color: rgba(97, 131, 122, 0.38);
          box-shadow:
            0 6px 14px rgba(49, 72, 66, 0.14),
            inset 0 1px 0 rgba(255, 255, 255, 1);
        }

        .topbar-progress-badges__daily:active {
          transform: translateY(0) scale(0.98);
        }
        .topbar-progress-badges__daily.is-revealing {
          border-color: rgba(75, 124, 246, 0.46);
          box-shadow: 0 0 0 6px rgba(75, 124, 246, 0.13), 0 6px 18px rgba(75, 124, 246, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.8);
          transition: border-color 320ms ease, box-shadow 320ms ease;
        }

        .topbar-progress-badges__daily.is-revealing::before,
        .topbar-progress-badges__daily.is-revealing::after {
          transition: width 1300ms cubic-bezier(0.22, 1, 0.36, 1), clip-path 1300ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .topbar-progress-badges__daily.is-complete {
          animation: topbarDailyComplete 640ms cubic-bezier(0.22, 1, 0.36, 1) 1150ms both;
        }

        @keyframes topbarDailyComplete {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(75, 124, 246, 0.34); }
          46% { transform: scale(1.075); box-shadow: 0 0 0 10px rgba(75, 124, 246, 0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(75, 124, 246, 0); }
        }

        .topbar-progress-badges__daily.is-holding::before,
        .topbar-progress-badges__daily.is-holding::after,
        .topbar-progress-badges__daily.is-holding .topbar-progress-badges__daily-label {
          transition: none;
        }
          
        .topbar-progress-badges__daily-content {
          position: relative;
          z-index: 1;
          min-width: 0;
          display: grid;
          align-items: center;
          justify-items: center;
          pointer-events: none;
        }

        .topbar-progress-badges__daily-reserve {
          grid-area: 1 / 1;
          visibility: hidden;
          font-family: var(--font-sans);
          font-size: var(--tb-badge-font, 12.5px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.01em;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          pointer-events: none;
        }

        .topbar-progress-badges__daily-label {
          grid-area: 1 / 1;
          display: block;
          color: #36495b;
          font-family: var(--font-sans);
          font-size: var(--tb-badge-font, 12.5px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.01em;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          opacity: 1;
          transform: translateY(var(--tb-badge-text-shift, 1px));
          transition: opacity 240ms cubic-bezier(0.22, 1, 0.36, 1), transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .topbar-progress-badges__daily-label.is-out {
          opacity: 0;
          transform: translateY(calc(var(--tb-badge-text-shift, 1px) + 3px));
        }

        .topbar-progress-badges__streak .streak-badge:focus-visible,
        .topbar-progress-badges__daily:focus-visible,
        .menu-button--home:focus-visible {
          outline: 2px solid #4f8f86;
          outline-offset: 2px;
        }

        @media (max-width: 390px) {

          .topbar-left--home {
            height: 36px;
            gap: 5px;
          }

          .menu-button--home {
            width: 36px;
            height: 36px;
            min-width: 36px;
            min-height: 36px;
            border-radius: 12px;
          }

          .topbar-progress-badges {
            --tb-badge-h: 34px;
            --tb-badge-px: 8px;
            --tb-badge-font: 11.5px;
            --tb-badge-gap: 4px;
            --tb-badge-emoji: 14px;
            height: 34px;
            gap: 5px;
          }

          .topbar-progress-badges__daily {
            min-width: 100px;
            height: 34px;
            min-height: 34px;
            padding-right: 8px;
            padding-left: 8px;
          }
        }

        @media (max-width: 340px) {
          .topbar-progress-badges {
            gap: 4px;
          }

          .topbar-progress-badges__streak {
            --tb-badge-px: 6px;
            --tb-badge-font: 11px;
            --tb-badge-emoji: 13px;
          }

          .topbar-progress-badges__daily {
            min-width: 76px;
            padding-right: 5px;
            padding-left: 5px;
          }

          .topbar-progress-badges__daily-content {
            padding-right: 5px;
            padding-left: 5px;
          }
          .topbar-progress-badges__daily-label,
          .topbar-progress-badges__daily-reserve {
            font-size: 10px;
            letter-spacing: -0.035em;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .topbar-progress-badges__daily-label {
            transition: none;
          }
        }
      `}</style>
    </>
  );
}